import { FetchHttpClient, HttpClient, HttpClientRequest, HttpClientResponse } from '@effect/platform';
import { Context, Effect, Layer } from 'effect';
import { getToken } from 'amplify/services';
import { getRuntimeConfig } from 'helpers/runtimeConfig';
import { CookieService } from 'services/cookie';

const makeCognitoHttpClient = Effect.gen(function* () {
  const cookieService = yield* CookieService;
  const locale = yield* cookieService.getLocale();

  const httpClient = (yield* HttpClient.HttpClient).pipe(
    HttpClient.mapRequestEffect(
      Effect.fn(function* (request) {
        const token = yield* Effect.promise(getToken);
        return request.pipe(
          HttpClientRequest.bearerToken(token), // disabled while using mock server not to expose token
          HttpClientRequest.prependUrl(`${getRuntimeConfig().env.REACT_APP_API_BASEURL}`),
          HttpClientRequest.setHeader('Accept-Language', locale),
        );
      }),
    ),
    // HttpClient.filterStatusOk, // every status except for 2xx will throw ResponseError
  );

  return { HttpClient: httpClient } as const;
}).pipe(Effect.provide(Layer.mergeAll(FetchHttpClient.layer, CookieService.Live)));

const makethirdPartyHttpClient = Effect.gen(function* () {
  const httpClient = (yield* HttpClient.HttpClient).pipe(
    HttpClient.withTracerPropagation(false), // Disable traceparent header to avoid CORS preflight
  );
  return {
    HttpClient: httpClient,
  };
}).pipe(Effect.provide(FetchHttpClient.layer));

export class CognitoHttpClient extends Context.Tag('CognitoHttpClient')<
  CognitoHttpClient,
  Effect.Effect.Success<typeof makeCognitoHttpClient>
>() {
  static readonly Live = Layer.effect(this, makeCognitoHttpClient);
  static readonly ThirdParty = Layer.effect(this, makethirdPartyHttpClient);
}

// Usage example
//
// Effect.provide(
//   Api.DefaultWithoutDependencies.pipe(
//     Layer.provide(
//       MockCognitoHttpClient(400, {
//         redirectUrl,
//         apiUrl,
//         isBeta: true,
//         phoneValidationRequired: true,
//       }),
//     ),
//   ),
// ),
// Effect.runPromise,
export const MockCognitoHttpClient = (status: number = 200, response: Object = {}, headers: Object = {}) =>
  Layer.succeed(CognitoHttpClient, {
    HttpClient: HttpClient.make(() => {
      const mockResponse = {
        status,
        headers,
        get json() {
          return Effect.succeed(response);
        },
      };
      return Effect.sleep('2 seconds').pipe(
        Effect.flatMap(() => Effect.succeed(mockResponse as unknown as HttpClientResponse.HttpClientResponse)),
      );
    }),
  });
